<template>
  <div>
    <rmr-modal
      :value="value"
      variant="form"
      :icon="icons.mdiCogOutline"
      :title="site ? `Configurer ${site.name}` : 'Configurer le site'"
      :subtitle="site ? site.domain : ''"
      eyebrow="Configuration CTC"
      max-width="640"
      @input="onVisibilityChange"
    >
      <template #body>
        <v-card-text class="ctc-site-modal__body">
          <div v-if="site" class="ctc-site-modal__prerequisites">
            <div class="ctc-site-modal__prerequisite">
              <span>Média Affilizz</span>
              <rmr-status-chip
                :label="site.hasAffilizzMedia ? 'Configuré' : 'Manquant'"
                :variant="site.hasAffilizzMedia ? 'success' : 'warning'"
                size="sm"
              />
            </div>
            <div class="ctc-site-modal__prerequisite">
              <span>Connecteur CMS</span>
              <rmr-status-chip
                :label="site.hasCmsConnector ? 'Connecté' : 'Manquant'"
                :variant="site.hasCmsConnector ? 'success' : 'warning'"
                size="sm"
              />
            </div>
          </div>

          <div class="ctc-site-modal__field">
            <label class="ctc-site-modal__label">Signature par défaut</label>
            <rmr-text-field
              v-model="draft.defaultSignature"
              :counter="signatureMaxLength"
              :error-messages="signatureErrors"
              :hide-details="!signatureErrors.length"
              maxlength="255"
              placeholder="Signature proposée lors de la création"
              size="md"
            />
            <p>
              Cette signature sera proposée par défaut et restera modifiable
              pour chaque article.
            </p>
          </div>

          <div class="ctc-site-modal__field">
            <label class="ctc-site-modal__label">Disponibilité</label>
            <rmr-toggle-card
              v-model="draft.isEnabled"
              label="Autoriser les nouvelles créations sur ce site"
              size="sm"
            />
            <p>
              Un site doit également disposer d’un média Affilizz et d’un
              connecteur CMS pour apparaître dans le parcours de création.
            </p>
          </div>
        </v-card-text>
      </template>

      <template #footer>
        <v-card-actions class="px-6 py-4">
          <v-spacer />
          <rmr-action-button
            label="Annuler"
            variant="ghost"
            :disabled="saving"
            @click="close"
          />
          <rmr-action-button
            label="Enregistrer"
            variant="primary"
            :icon="icons.mdiContentSaveOutline"
            :loading="saving"
            :disabled="!canSave"
            @click="requestSave"
          />
        </v-card-actions>
      </template>
    </rmr-modal>

    <rmr-confirm-dialog
      v-model="disableConfirmation"
      title="Désactiver ce site pour le CTC ?"
      message="Le site disparaîtra du parcours de création pour toute l’équipe. Les brouillons existants resteront visibles et leurs reprises Affilizz possibles. Un parcours non enregistré en cours sur ce site sera réinitialisé."
      confirm-label="Désactiver"
      danger
      @confirm="save"
    />
  </div>
</template>

<script>
import { computed, reactive, ref, watch } from "@vue/composition-api";
import { mdiCogOutline, mdiContentSaveOutline } from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import RmrStatusChip from "@/components/ui/feedback/RmrStatusChip.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import RmrToggleCard from "@/components/ui/forms/RmrToggleCard.vue";
import RmrModal from "@/components/ui/layout/RmrModal.vue";

import { SIGNATURE_MAX_LENGTH } from "../constants";
import {
  SIGNATURE_PLAIN_TEXT_MESSAGE,
  isPlainTextSignature,
} from "../ctc-model";

export default {
  name: "CtcSiteConfigurationModal",
  components: {
    RmrActionButton,
    RmrConfirmDialog,
    RmrModal,
    RmrStatusChip,
    RmrTextField,
    RmrToggleCard,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    site: {
      type: Object,
      default: null,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const draft = reactive({ defaultSignature: "", isEnabled: true });
    const disableConfirmation = ref(false);

    watch(
      () => props.site,
      (site) => {
        draft.defaultSignature = site ? site.defaultSignature : "";
        draft.isEnabled = site ? site.isEnabled : true;
      },
      { immediate: true }
    );

    const signatureErrors = computed(() => {
      const value = (draft.defaultSignature || "").trim();
      if (!value) return ["La signature est obligatoire."];
      if (value.length > SIGNATURE_MAX_LENGTH) {
        return [`Maximum ${SIGNATURE_MAX_LENGTH} caractères.`];
      }
      if (!isPlainTextSignature(value)) return [SIGNATURE_PLAIN_TEXT_MESSAGE];
      return [];
    });
    const isDirty = computed(
      () =>
        !!props.site &&
        ((draft.defaultSignature || "").trim() !==
          props.site.defaultSignature ||
          draft.isEnabled !== props.site.isEnabled)
    );
    const close = () => {
      if (!props.saving) emit("input", false);
    };
    const save = () => {
      if (!props.site || signatureErrors.value.length) return;
      emit(
        "save",
        {
          id: props.site.id,
          defaultSignature: draft.defaultSignature.trim(),
          isEnabled: draft.isEnabled,
        },
        (success) => {
          if (success) close();
        }
      );
    };
    const requestSave = () => {
      if (props.site.isEnabled && !draft.isEnabled) {
        disableConfirmation.value = true;
        return;
      }
      save();
    };

    return {
      canSave: computed(
        () => isDirty.value && !signatureErrors.value.length && !props.saving
      ),
      close,
      disableConfirmation,
      draft,
      icons: { mdiCogOutline, mdiContentSaveOutline },
      onVisibilityChange: (visible) => {
        if (!visible) close();
      },
      requestSave,
      save,
      signatureErrors,
      signatureMaxLength: SIGNATURE_MAX_LENGTH,
    };
  },
};
</script>

<style lang="scss" scoped>
.ctc-site-modal__body {
  display: grid;
  gap: 24px;
  padding: 24px;
}

.ctc-site-modal__prerequisites {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.ctc-site-modal__prerequisite {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid $rmr-border;
  border-radius: $rmr-radius-xl;
  background: $rmr-surface-2;
  color: $rmr-text-secondary;
  font-size: $rmr-fs-sm;
  font-weight: 700;
}

.ctc-site-modal__field {
  display: grid;
  gap: 8px;
}

.ctc-site-modal__label {
  color: $rmr-text-primary;
  font-size: $rmr-fs-sm;
  font-weight: 800;
}

.ctc-site-modal__field p {
  margin: 0;
  color: $rmr-text-muted;
  font-size: $rmr-fs-xs;
  line-height: 1.45;
}

@media (max-width: 600px) {
  .ctc-site-modal__prerequisites {
    grid-template-columns: 1fr;
  }
}
</style>
